import {cloneDeep, map} from 'lodash';

import generateGUID from 'src/js/common/utils/GUID';

const TABLE_DATA_COLUMNS = 'columns';
const TABLE_DATA_ROWS = 'rows';

export const TABLE_DATA_COLUMN_KEY = 'key';
export const TABLE_DATA_COLUMN_NAME = 'name';
export const TABLE_DATA_COLUMN_WIDTH = 'width';
export const TABLE_DATA_COLUMN_EDITABLE = 'editable';
export const TABLE_DATA_COLUMN_RESIZABLE = 'resizable';

export class TableDataColumn {
  constructor(rawData = {}, id = undefined) {
    this.rawData = rawData;
    this.id = id;
  }

  clone() {
    return Object.assign(
      new TableDataColumn(cloneDeep(this.rawData), this.getId()),
      this
    );
  }

  getId() {
    return this.id;
  }

  getRawData() {
    return this.rawData;
  }

  getKey() {
    return this.rawData[TABLE_DATA_COLUMN_KEY];
  }

  getName() {
    return this.rawData[TABLE_DATA_COLUMN_NAME] || this.getKey();
  }

  getWidth() {
    return this.rawData[TABLE_DATA_COLUMN_WIDTH];
  }

  getEditable() {
    return this.rawData[TABLE_DATA_COLUMN_EDITABLE] || false;
  }

  getResizable() {
    return this.rawData[TABLE_DATA_COLUMN_RESIZABLE] || false;
  }
}

export class TableDataRow {
  constructor(rawData = {}, id = undefined) {
    this.rawData = rawData;
    this._id = id;
  }

  clone() {
    return Object.assign(
      new TableDataRow(cloneDeep(this.rawData), this.getId()),
      this
    );
  }

  getId() {
    return this._id;
  }

  getRawData() {
    return this.rawData;
  }
}

export class TableDataColumnMapped {
  constructor(column) {
    this.column = column;
    this[TABLE_DATA_COLUMN_KEY] = this.column.getKey();
    this[TABLE_DATA_COLUMN_NAME] = this.column.getName();
    this[TABLE_DATA_COLUMN_WIDTH] = this.column.getWidth();
    this[TABLE_DATA_COLUMN_EDITABLE] = this.column.getEditable();
    this[TABLE_DATA_COLUMN_RESIZABLE] = this.column.getResizable();
  }

  clone() {
    return Object.assign(
      new TableDataColumnMapped(cloneDeep(this.column)),
      this
    );
  }

  getId() {
    return this.column.getId();
  }

  getKey() {
    return this[TABLE_DATA_COLUMN_KEY];
  }
}

export class TableDataRowMapped {
  constructor(row, columnKeys) {
    this.row = row;
    this.columnKeys = columnKeys;
    if (columnKeys) {
      columnKeys.forEach((columnKey) => {
        const columnValue = this.row.getRawData()[columnKey];
        this[columnKey] = columnValue !== undefined ? columnValue : '';
      });
    }
  }

  clone() {
    return Object.assign(
      new TableDataRowMapped(cloneDeep(this.row), this.columnKeys),
      this
    );
  }

  getId() {
    return this.row.getId();
  }
}

export class TableData {
  static initColumns(rawDataColumns) {
    const columns = [];

    if (rawDataColumns) {
      rawDataColumns.forEach((column) => {
        columns.push(new TableDataColumn(column, generateGUID()));
      });
    }
    return columns;
  }

  static initRows(rawDataRows) {
    const rows = [];

    if (rawDataRows) {
      rawDataRows.forEach((row) => {
        rows.push(new TableDataRow(row, generateGUID()));
      });
    }
    return rows;
  }

  static initMappedColumns(columns) {
    const mappedColumns = [];

    if (columns) {
      columns.forEach((column) => {
        mappedColumns.push(new TableDataColumnMapped(column));
      });
    }
    return mappedColumns;
  }

  static initMappedRows(rows, columns) {
    const mappedRows = [];
    const columnKeys = map(columns, (column) => column.getKey());

    if (rows) {
      rows.forEach((row) => {
        mappedRows.push(new TableDataRowMapped(row, columnKeys));
      });
    }
    return mappedRows;
  }

  static needToEscapeCsv(value, delimiter) {
    return (
      value.indexOf(delimiter) !== -1 ||
      value.indexOf('"') !== -1 ||
      value.indexOf('\n') !== -1 ||
      value.indexOf('\r') !== -1
    );
  }

  static exportToCsvString(columns, rows, delimiter = ',') {
    const columnKeys = map(columns, (column) => column.getKey());
    let resultString = '';
    if (rows) {
      for (let rowIdx = 0, len = rows.length; rowIdx < len; rowIdx++) {
        const row = rows[rowIdx];
        for (
          let colIdx = 0, rowLen = columnKeys.length;
          colIdx < rowLen;
          colIdx++
        ) {
          // convert value to string
          let colValue = `${
            row[columnKeys[colIdx]] !== undefined ? row[columnKeys[colIdx]] : ''
          }`;
          if (TableData.needToEscapeCsv(colValue, delimiter)) {
            // duplicate double quotes in value
            colValue = colValue.split('"').join('""');
            // enclose value in double quotes
            colValue = `"${colValue}"`;
          }
          resultString += colValue;
          if (colIdx < rowLen - 1) {
            resultString += delimiter;
          }
        }
        if (rowIdx < len - 1) {
          resultString += '\n';
        }
      }
    }
    return resultString;
  }

  static exportToRawArrayData(columns, rows, options = {headerInData: false}) {
    let resultData = [];
    const columnKeys = map(columns, (column) => column.getKey());
    if (rows) {
      for (let rowIdx = 0, len = rows.length; rowIdx < len; rowIdx++) {
        const row = rows[rowIdx];
        resultData.push([]);
        for (
          let colIdx = 0, rowLen = columnKeys.length;
          colIdx < rowLen;
          colIdx++
        ) {
          // convert value to string
          const colValue = `${
            row[columnKeys[colIdx]] !== undefined ? row[columnKeys[colIdx]] : ''
          }`;
          // save in result
          resultData[rowIdx].push(colValue);
        }
      }
    }
    if (options.headerInData) {
      const columnNames = map(columns, (column) => column.getName());
      resultData = [columnNames, ...resultData];
    }

    return resultData;
  }

  constructor(rawData = {}) {
    this.rawData = rawData;
    this.columns = TableData.initColumns(this.rawData[TABLE_DATA_COLUMNS]);
    this.rows = TableData.initRows(this.rawData[TABLE_DATA_ROWS]);
    this.mappedColumns = TableData.initMappedColumns(
      cloneDeep(this.getColumns())
    );
    this.mappedRows = TableData.initMappedRows(
      cloneDeep(this.getRows()),
      this.getColumns()
    );
  }

  getColumns() {
    return this.columns;
  }

  getMappedColumns() {
    return this.mappedColumns;
  }

  getRows() {
    return this.rows;
  }

  getMappedRows() {
    return this.mappedRows;
  }
}

export default {TableData, TableDataColumn};
